import { HttpErrorResponse } from '@angular/common/http';
import moment from "moment";
import { ApiError } from "../models/api.response";

export class Utils {
  public static formatDate(date: any): Date {
    
    if (date === undefined || date === null) {
      return undefined;
    } else {
      return moment(date).toDate();
    }
  }


  public static formatDateTime(date: any): Date {
    if (date === undefined || date === null) {
      return undefined;
    } else {
      return new Date(moment.utc(date, ["YYYY-MM-DD h:mm A"]).local().format("YYYY-MM-DD hh:mm A"));
    }
  }

  public static toTitleCase(input: string): string {
    return input.toLowerCase().replace(/(?:^|\s)\w/g, (match) => match.toUpperCase());
  }

  public static capitalizeFirstLetter(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  /**
   * Meta values normally arrive as text, but some responses send numbers (e.g. an AD user ID
   * 41822) or lists. Convert first: calling .replace on a number threw and stopped the whole
   * details dialog from rendering after its first field.
   */
  public static formatMetaValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '';
    const text = Array.isArray(value) ? value.join(', ') : String(value);
    return text.replace(/&#13;/g, '<br>');
  }  

  public static cleanQuillHtml(html: string): string {
    if (!html) return html;  
    let processedHtml = html;
    processedHtml = this.convertQuillListsToNested(processedHtml);
    processedHtml = processedHtml.replace(/<li[^>]*data-list="([^"]*)"[^>]*>/gi, (match) => {
      return match.replace(/\s*data-list="[^"]*"/gi, '');
    });
    
    // Replace <p><br></p> or <p><br/></p> with just <br> to preserve line breaks
    // This allows users to add intentional line breaks in the editor
    processedHtml = processedHtml.replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '<br>');
    
    // Convert empty paragraphs between content paragraphs to <br> to preserve spacing
    // Pattern: <p>content</p><p></p><p>content</p> -> <p>content</p><br><p>content</p>
    // This handles empty paragraphs (with or without whitespace) between content
    processedHtml = processedHtml.replace(/(<\/p>)\s*(<p[^>]*>\s*<\/p>)\s*(<p[^>]*>)/gi, '$1<br>$3');
    
    // Remove completely empty paragraphs at the start (no content before)
    processedHtml = processedHtml.replace(/^(<p[^>]*>\s*<\/p>\s*)+/gi, '');
    
    // Remove completely empty paragraphs at the end (no content after)
    processedHtml = processedHtml.replace(/(<p[^>]*>\s*<\/p>\s*)+$/gi, '');
    
    // Remove remaining standalone empty paragraphs (not between content, not at start/end)
    // These are empty paragraphs that are not between two content paragraphs
    processedHtml = processedHtml.replace(/<p[^>]*>\s*(&nbsp;|\u00A0)*\s*<\/p>/gi, '');
    
    // Convert consecutive <p> tags into a single <p> with <br> tags
    // This handles the case where pressing Enter creates separate paragraphs
    // Pattern: <p>content</p><p>content</p> -> <p>content<br>content</p>
    // But preserve <br> tags between paragraphs (don't merge if <br> exists)
    processedHtml = this.mergeConsecutiveParagraphs(processedHtml);
    
    processedHtml = processedHtml.replace(/<p([^>]*)>/gi, (match, attributes) => {
      if (attributes && /style\s*=/i.test(attributes)) {
        return match.replace(/style\s*=\s*["']([^"']*)["']/i, (styleMatch, styleValue) => {
          if (!/margin\s*:/i.test(styleValue)) {
            return `style="${styleValue}; margin: 0;"`;
          }

          return styleMatch.replace(/margin\s*:\s*[^;]+/i, 'margin: 0');
        });
      } else {
        return `<p${attributes} style="margin: 0;">`;
      }
    });
    
    return processedHtml;
  }

  private static mergeConsecutiveParagraphs(html: string): string {
    // Only merge paragraphs that are directly adjacent (no <br> between them)
    // Pattern: <p>content</p><p>content</p> (no <br> in between)
    const paragraphGroupRegex = /(<p[^>]*>[\s\S]*?<\/p>)(\s*<p[^>]*>[\s\S]*?<\/p>)+/gi;
    
    return html.replace(paragraphGroupRegex, (match) => {
      // Check if there's a <br> tag in the match - if so, don't merge
      if (/<br\s*\/?>/i.test(match)) {
        return match; // Return as-is if <br> exists (preserve spacing)
      }
      
      const paragraphRegex = /<p[^>]*>([\s\S]*?)<\/p>/gi;
      const contents: string[] = [];
      let paraMatch;
      
      while ((paraMatch = paragraphRegex.exec(match)) !== null) {
        const content = paraMatch[1].trim();
        if (content && content !== '' && !/^(&nbsp;|\u00A0|\s)*$/.test(content)) {
          contents.push(content);
        }
      }
      
      // If we have multiple paragraphs, merge them with <br> tags
      if (contents.length > 1) {
        return `<p>${contents.join('<br>')}</p>`;
      } else if (contents.length === 1) {
        return `<p>${contents[0]}</p>`;
      }
      
      return '';
    });
  }

  private static convertQuillListsToNested(html: string): string {
    const listRegex = /<(ul|ol)[^>]*>([\s\S]*?)<\/\1>/gi;
    return html.replace(listRegex, (match, listType, content) => {
      const items: Array<{ html: string; indent: number }> = [];
      const itemRegex = /<li[^>]*class="[^"]*ql-indent-(\d+)[^"]*"[^>]*>([\s\S]*?)<\/li>|<li[^>]*>([\s\S]*?)<\/li>/gi;
      let itemMatch;
      while ((itemMatch = itemRegex.exec(content)) !== null) {
        const indent = itemMatch[1] ? parseInt(itemMatch[1], 10) : 0;
        const itemHtml = itemMatch[2] || itemMatch[3] || '';
        items.push({ html: itemHtml.trim(), indent });
      }
      if (items.length === 0) return match;
      let result = '';
      let i = 0;
      const processLevel = (targetIndent: number, parentListType: string, parentListStyle?: string): void => {
        while (i < items.length) {
          const item = items[i];
          if (item.indent < targetIndent) {
            return;
          }
          if (item.indent === targetIndent) {
            if (i > 0 && items[i - 1].indent === targetIndent) {
              result += '</li>';
            }
            result += '<li>' + item.html;
            i++;
            if (i < items.length && items[i].indent > targetIndent) {
              let nestedType = 'ol';
              let nestedAttrs = '';
              
              if (parentListType === 'ol') {
                nestedAttrs = ' type="a"';
              } else {
                nestedAttrs = '';
              }
              result += '<' + nestedType + nestedAttrs + '>';
              processLevel(targetIndent + 1, nestedType, nestedAttrs);
              result += '</' + nestedType + '>';
            }
          } else {
            return;
          }
        }
      };
      processLevel(0, listType);
      if (items.length > 0) {
        result += '</li>';
      }
      return '<' + listType + '>' + result + '</' + listType + '>';
    });
  }
      
  public static unwrapError(error: unknown): unknown {
    let current = error;

    for (let depth = 0; depth < 3; depth++) {
      if (!current || typeof current !== 'object') {
        break;
      }

      const wrapped = current as Record<string, unknown>;
      if (wrapped['rejection']) {
        current = wrapped['rejection'];
        continue;
      }
      if (wrapped['ngOriginalError']) {
        current = wrapped['ngOriginalError'];
        continue;
      }
      break;
    }

    return current;
  }

  /** Returns a user-facing message only for API / application-defined errors. */
  public static getUserFacingErrorMessage(error: unknown): string | null {
    const unwrapped = this.unwrapError(error);

    if (unwrapped instanceof HttpErrorResponse) {
      return this.getApiResponseErrorMessage(unwrapped.error);
    }

    if (Array.isArray(unwrapped) && unwrapped.length > 0 && (unwrapped[0] as ApiError)?.text) {
      return this.formatApiErrorTexts(unwrapped as ApiError[]);
    }

    if (typeof unwrapped === 'object' && unwrapped !== null) {
      if (this.isRuntimeError(unwrapped)) {
        return null;
      }

      const apiError = unwrapped as { errors?: ApiError[]; message?: string };
      if (Array.isArray(apiError.errors) && apiError.errors.length > 0) {
        return this.formatApiErrorTexts(apiError.errors);
      }
      if (typeof apiError.message === 'string' && apiError.message.trim() !== '') {
        return apiError.message;
      }
    }

    return null;
  }

  /** True for native JS errors (TypeError, etc.), not API error payloads. */
  public static isRuntimeError(value: unknown): boolean {
    if (value instanceof Error) {
      return !(value instanceof HttpErrorResponse);
    }

    if (typeof value !== 'object' || value === null) {
      return false;
    }

    const candidate = value as Record<string, unknown>;
    return (
      typeof candidate['message'] === 'string' &&
      typeof candidate['stack'] === 'string' &&
      !Array.isArray(candidate['errors'])
    );
  }

  /** HTTP-layer errors already surfaced by HttpErrorInterceptor. */
  public static isHttpLayerError(error: unknown): boolean {
    const unwrapped = this.unwrapError(error);

    if (unwrapped instanceof HttpErrorResponse) {
      return true;
    }

    return Array.isArray(unwrapped) && unwrapped.length > 0 && !!(unwrapped[0] as ApiError)?.text;
  }

  public static getErrorMessage(error: any, defaultMessage: string): string {
    const apiMessage = this.getUserFacingErrorMessage(error);
    if (apiMessage) {
      return apiMessage;
    }

    if (error?.message && typeof error.message === 'string' && error.message.trim() !== '') {
      return error.message;
    }

    return defaultMessage;
  }

  private static getApiResponseErrorMessage(body: unknown): string | null {
    if (!body) {
      return null;
    }

    if (Array.isArray(body) && body.length > 0 && (body[0] as ApiError)?.text) {
      return this.formatApiErrorTexts(body as ApiError[]);
    }

    if (typeof body === 'object') {
      if (this.isRuntimeError(body)) {
        return null;
      }

      const apiBody = body as { errors?: ApiError[]; message?: string };
      if (Array.isArray(apiBody.errors) && apiBody.errors.length > 0) {
        return this.formatApiErrorTexts(apiBody.errors);
      }
      if (typeof apiBody.message === 'string' && apiBody.message.trim() !== '') {
        return apiBody.message;
      }
    }

    return null;
  }

  private static formatApiErrorTexts(errors: ApiError[]): string | null {
    const errorTexts = errors
      .map((entry: ApiError) => entry.text)
      .filter((text: string) => text && text.trim() !== '' && text !== 'Unknown error.');

    return errorTexts.length > 0 ? errorTexts.join(' ') : null;
  }
}